import './TicketDetailPage.scss';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useLang } from '../../i18n';
import { useToast } from '../../components/Toast';
import { get, post, patch } from '../../services/api';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Textarea from '../../components/Textarea';
import Skeleton from '../../components/Skeleton';

interface TicketComment {
  id: number | string;
  author_type?: 'customer' | 'admin' | 'system';
  author_id?: string;
  body: string;
  created_at: string;
}

interface Ticket {
  public_id: string;
  subject: string;
  body?: string;
  body_md?: string;
  status: string;
  priority: string;
  assignee_id?: number | null;
  due_at?: string | null;
  resolved_at?: string | null;
  csat_score?: number | null;
}

const POLL_MS = 6000;

export default function TicketDetailPage() {
  const { public_id } = useParams<{ public_id: string }>();
  const { t } = useLang();
  const toast = useToast();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [rating, setRating] = useState(0);
  const [csatComment, setCsatComment] = useState('');

  const loadTicket = async () => {
    if (!public_id) return;
    try {
      const res = await get<Ticket>(`/me/tickets/${public_id}/`);
      setTicket(res);
    } catch {
      setTicket(null);
    }
  };

  const loadComments = async () => {
    if (!public_id) return;
    try {
      const res = await get<TicketComment[] | { results: TicketComment[] }>(`/me/tickets/${public_id}/comments/`);
      const list = Array.isArray(res) ? res : (res.results || []);
      // optimization: re-set только если набор изменился
      setComments((cur) => {
        if (cur.length === list.length && cur[cur.length - 1]?.id === list[list.length - 1]?.id) return cur;
        return list;
      });
    } catch { void 0; }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadTicket(), loadComments()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    const id = window.setInterval(loadComments, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [public_id]);

  const addComment = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || !public_id) return;
    setSending(true);
    try {
      const created = await post<TicketComment>(`/me/tickets/${public_id}/comments/`, { body: draft.trim() });
      setComments((arr) => [...arr, created]);
      setDraft('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!public_id) return;
    try {
      const updated = await patch<Ticket>(`/me/tickets/${public_id}/`, { status: 'resolved' });
      setTicket(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const submitCsat = async () => {
    if (!public_id || rating === 0) return;
    try {
      await post(`/me/tickets/${public_id}/csat/`, { rating, comment: csatComment });
      toast.success(t('profile.saved'));
      setTicket((cur) => cur ? { ...cur, csat_score: rating } : cur);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  if (loading) return <Skeleton height={300} />;
  if (!ticket) return <Card padding="lg">{t('common.error')}</Card>;

  const description = ticket.body || ticket.body_md;

  return (
    <div className="ticket-page">
      <Card padding="lg" className="ticket-page__head">
        <div className="ticket-page__title-row">
          <h1 className="ticket-page__title">{ticket.subject}</h1>
          <div className="ticket-page__badges">
            <Badge variant="brand">{ticket.priority}</Badge>
            <Badge variant="info">{ticket.status}</Badge>
          </div>
        </div>
        {description ? (
          <>
            <hr className="ticket-page__divider" />
            <p className="ticket-page__body">{description}</p>
          </>
        ) : null}
        <hr className="ticket-page__divider" />
        <dl className="ticket-page__meta">
          <div className="ticket-page__meta-row">
            <dt>{t('ticket.assignee')}</dt>
            <dd>{ticket.assignee_id ? `#${ticket.assignee_id}` : '—'}</dd>
          </div>
          <div className="ticket-page__meta-row">
            <dt>{t('ticket.sla')}</dt>
            <dd>{ticket.due_at ? new Date(ticket.due_at).toLocaleString() : '—'}</dd>
          </div>
        </dl>
        {ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
          <div>
            <Button variant="secondary" size="md" onClick={closeTicket}>{t('ticket.close')}</Button>
          </div>
        ) : null}
      </Card>

      <Card padding="md">
        <ul className="ticket-page__comments">
          {comments.length === 0 ? (
            <li style={{ color: 'var(--c-ink-500, #6B6F7D)', fontSize: 13, padding: 12 }}>—</li>
          ) : comments.map((c, i) => (
            <li key={c.id} className="ticket-page__comment">
              {i > 0 ? <hr className="ticket-page__divider" /> : null}
              <div className="ticket-page__comment-inner">
                <div className="ticket-page__comment-head">
                  <span className="ticket-page__comment-author">{c.author_type === 'admin' ? 'Поддержка Ukam IT' : 'Вы'}</span>
                  <span className="ticket-page__comment-time">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="ticket-page__comment-body">{c.body}</div>
              </div>
            </li>
          ))}
        </ul>
        {ticket.status !== 'closed' ? (
          <>
            <hr className="ticket-page__divider" />
            <form className="ticket-page__form" onSubmit={addComment}>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('ticket.comment.placeholder')}
                rows={4}
              />
              <Button type="submit" variant="primary" size="md" loading={sending}>{t('ticket.comment.send')}</Button>
            </form>
          </>
        ) : null}
      </Card>

      {ticket.status === 'resolved' && !ticket.csat_score ? (
        <Card padding="lg">
          <h3 className="ticket-page__csat-title">{t('ticket.csat.title')}</h3>
          <div className="ticket-page__stars" role="radiogroup" aria-label={t('ticket.csat.title')}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                className={['ticket-page__star', rating >= n ? 'ticket-page__star--active' : ''].filter(Boolean).join(' ')}
                onClick={() => setRating(n)}
              >
                ★
              </button>
            ))}
          </div>
          <Textarea
            label={t('ticket.csat.comment')}
            value={csatComment}
            onChange={(e) => setCsatComment(e.target.value)}
            rows={3}
          />
          <div style={{ marginTop: 'var(--s-3)' }}>
            <Button variant="primary" size="md" disabled={rating === 0} onClick={submitCsat}>
              {t('ticket.csat.submit')}
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
